<template>
  <v-sheet color="surface" rounded="lg" class="pa-4 appeal-section">
    <v-container :fluid="true">
      <div v-if="errorMsg" class="text-error mb-2">{{ errorMsg }}</div>

      <v-alert v-if="sentInfo" type="success" variant="tonal" density="compact" class="mb-4">
        Appeal sent {{ sentInfo.sentAtLabel }}<span v-if="sentInfo.sentByName"> by {{ sentInfo.sentByName }}</span
        >.
        <a v-if="sentInfo.sentDocumentUrl" :href="sentInfo.sentDocumentUrl" target="_blank" class="text-primary">
          View sent document
        </a>
      </v-alert>

      <!-- No draft yet: intro + optional attachments + generate -->
      <template v-if="!hasDraft">
        <p class="appeal-section__intro mb-4">
          Generate a draft appeal letter from this patient's record and the denial reason. You can optionally attach
          additional patient documents or clinical guidelines to strengthen the appeal.
        </p>
        <div class="d-flex flex-wrap align-center mb-4">
          <div v-for="(doc, i) in documentUrls" :key="doc.link" class="d-flex align-center mr-2 mb-1">
            <v-icon size="small" class="mr-1">mdi-paperclip</v-icon>
            <span class="text-body-2">{{ doc.fileName || "Document" }}</span>
            <v-btn icon="mdi-close" variant="text" size="x-small" @click="removeDocument(i)" />
          </div>
          <v-btn
            tag="label"
            variant="tonal"
            color="primary"
            rounded
            size="small"
            prepend-icon="mdi-paperclip"
            :loading="uploading"
            :disabled="generating">
            Attach a document
            <input type="file" accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg" class="d-none" @change="onPickFile" />
          </v-btn>
        </div>
        <v-btn
          color="primary"
          variant="flat"
          rounded
          prepend-icon="mdi-creation"
          :loading="generating"
          @click="onGenerate">
          Generate appeal letter
        </v-btn>
      </template>

      <!-- Draft exists: editor + attach/regenerate in the editor's bottom action row -->
      <OutgoingFaxEditor
        v-else
        :title="''"
        :order-info="order"
        :to-fax-number="''"
        :cancel-button="false"
        :appeal="true"
        :extra-file-links="documentUrls.map((d) => d.link)"
        send-button-text="Send Appeal"
        :frontend-template="appealTemplate"
        @update:content="onContentEdit"
        @on-complete="onAppealComplete">
        <template #attach>
          <v-col cols="auto" class="d-flex flex-wrap align-center pa-0">
            <div v-for="(doc, i) in documentUrls" :key="doc.link" class="d-flex align-center mr-2">
              <v-icon size="small" class="mr-1">mdi-paperclip</v-icon>
              <span class="text-body-2">{{ doc.fileName || "Document" }}</span>
              <v-btn icon="mdi-close" variant="text" size="x-small" @click="removeDocument(i)" />
            </div>
            <v-btn
              tag="label"
              variant="tonal"
              color="primary"
              rounded
              prepend-icon="mdi-paperclip"
              :loading="uploading"
              :disabled="generating">
              Attach a document
              <input type="file" accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg" class="d-none" @change="onPickFile" />
            </v-btn>
            <v-btn
              variant="text"
              color="primary"
              class="ml-2"
              prepend-icon="mdi-refresh"
              :loading="generating"
              @click="onGenerate">
              Regenerate
            </v-btn>
          </v-col>
        </template>
      </OutgoingFaxEditor>
    </v-container>
  </v-sheet>
</template>

<script setup>
import { ref, computed, onUnmounted } from "vue";
import { orderInfoService } from "@/services/orderInfoService";
import { uuid } from "vue-uuid";
import { uploadService, validateFile, parseUploadError } from "@/services/uploadService";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["appealSent"]);

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const SAVE_DEBOUNCE_MS = 1000;

const fileNameFromUrl = (url) => (url.split("/").pop() || "Document").split("?")[0];

// Seed attachments + draft from the persisted appeal letter so both survive a reload
// (and a later Regenerate re-sends the same attachments).
const documentUrls = ref(
  (props.order.appealLetter?.attachedDocumentUrls || []).map((link) => ({ link, fileName: fileNameFromUrl(link) }))
);
const uploading = ref(false);
const generating = ref(false);
const generatedText = ref(props.order.appealLetter?.draftText || "");
const errorMsg = ref("");
let pollTimer = null;
let saveTimer = null;

const hasDraft = computed(() => !!generatedText.value);

const appealTemplate = computed(() => ({
  name: "AI Appeal Draft",
  content: generatedText.value,
  category: "appeal",
}));

const sentInfo = computed(() => {
  const a = props.order.appealLetter;
  if (!a?.sentAt) return null;
  return {
    sentAtLabel: new Date(a.sentAt).toLocaleString(),
    sentByName: a.sentByName,
    sentDocumentUrl: a.sentDocumentUrl,
  };
});

const onPickFile = async (event) => {
  const file = event.target.files?.[0];
  if (event.target) event.target.value = "";
  if (!file) return;
  const validationError = validateFile(file);
  if (validationError) {
    errorMsg.value = validationError;
    return;
  }
  uploading.value = true;
  errorMsg.value = "";
  try {
    const link = await uploadService.uploadFile(file, `${uuid.v4()}/${file.name}`);
    documentUrls.value.push({ link, fileName: file.name });
  } catch (e) {
    errorMsg.value = parseUploadError(e);
  } finally {
    uploading.value = false;
  }
};

const removeDocument = (index) => {
  documentUrls.value.splice(index, 1);
};

const clearPoll = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

const onGenerate = async () => {
  errorMsg.value = "";
  generating.value = true;
  try {
    const { taskId } = await orderInfoService.draftAppeal(props.order.orderKey, {
      documentUrls: documentUrls.value.map((d) => d.link),
    });
    pollForDraft(taskId, Date.now());
  } catch (e) {
    generating.value = false;
    errorMsg.value = `Failed to start appeal generation: ${e.message || e}`;
  }
};

const pollForDraft = (taskId, startedAt) => {
  clearPoll();
  pollTimer = setTimeout(async () => {
    try {
      const result = await orderInfoService.getAppealDraft(props.order.orderKey, taskId);
      if (result.status === "done") {
        generatedText.value = result.draftText || "";
        generating.value = false;
        return;
      }
      if (result.status === "error") {
        errorMsg.value = result.error || "Appeal drafting failed. Please try again.";
        generating.value = false;
        return;
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        errorMsg.value = "Appeal drafting timed out. Please try again.";
        generating.value = false;
        return;
      }
      pollForDraft(taskId, startedAt);
    } catch (e) {
      errorMsg.value = `Failed to fetch appeal draft: ${e.message || e}`;
      generating.value = false;
    }
  }, POLL_INTERVAL_MS);
};

// Persist user edits to the draft (debounced) so they survive reloads.
const onContentEdit = (value) => {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    orderInfoService.saveAppealDraft(props.order.orderKey, { draftText: value }).catch((e) => {
      errorMsg.value = `Failed to save draft: ${e.message || e}`;
    });
  }, SAVE_DEBOUNCE_MS);
};

const onAppealComplete = ({ sent }) => {
  if (sent) {
    emit("appealSent", { sent: true });
  }
};

onUnmounted(() => {
  clearPoll();
  if (saveTimer) clearTimeout(saveTimer);
});
</script>

<style lang="scss" scoped>
.appeal-section {
  border: 1px solid $lh_dash_border;
  box-shadow: $lh_shadow_sm;
  border-radius: 16px !important;

  :deep(.v-btn) {
    letter-spacing: normal;
    text-transform: none;
    font-weight: 500;
  }
}

.appeal-section__intro {
  font-family: $lh_font_family_dm_sans;
  color: $lh_color_navy_dark;
}
</style>
